<script setup lang="ts">
import type { EChartsOption } from 'echarts'
import type { CSSProperties } from 'vue'
import { useEcharts, type ChartProxy } from '~/composables/useEcharts'
import { getRatingChart, getRatingDetail, type RatingAdjustmentDetail, type RatingAdjustmentItem, type RatingBusinessDetail, type RatingBusinessItem, type RatingChartType, type RatingDetailPayload, type RatingSmallScore, type RatingTrendSeries } from '~/server/exchange-detail'

const { t } = useAppI18n()
const { init } = useEcharts()
const EMPTY_ADJUSTMENT_DETAIL: RatingAdjustmentDetail = {}
const props = withDefaults(
  defineProps<{
    ratingCards?: {
      value?: string | number
      time?: string
      tips?: string
    }
    exchangeName?: string
    exchangeId?: string
    ultimateType?: number | string | null
  }>(),
  {
    ratingCards: () => ({}),
    exchangeName: '',
    exchangeId: '',
    ultimateType: null,
  },
)
const emit = defineEmits<{
  'open-iap': []
}>()

  /** 数据缺失时的占位文本 */
const emptyText = '--'
  /** 评分数值范围及进度条显示阈值 */
const MIN_VISIBLE_PROGRESS_SCORE = 0.7
const MAX_NOTCHED_PROGRESS_SCORE = 9.5
const MAX_RATING_SCORE = 10
const RATING_ANIMATION_DURATION = 1500
const MIN_VISIBLE_RADAR_SCORE = 0.5
const RADAR_LOW_SCORE_THRESHOLD = 2
const RADAR_CENTER_MARKER_IMAGE =
  'https://resources1.interface003.com/web30/2026/07/02/fffb7402-d913-49be-a700-0dd92fe80a01.png'
type SsrRatingAnimationState = {
  currentScore: number
}

/** 将评分约束在天眼评分的合法范围内。 */
const clampRatingScore = (value: number) => Math.min(Math.max(value, 0), MAX_RATING_SCORE)

/** 读取 SSR 脚本正在绘制的数值，确保 hydration 的首个虚拟节点与 DOM 一致。 */
const getSsrRatingAnimationCurrentScore = () => {
  if (!import.meta.client) return 0

  const animationState = (window as Window & { __exchangeRatingScoreAnimation?: SsrRatingAnimationState }).__exchangeRatingScoreAnimation
  const currentScore = animationState?.currentScore
  return typeof currentScore === 'number' && Number.isFinite(currentScore) ? clampRatingScore(currentScore) : 0
}
  /** UI 状态 - 进度条动画、弹窗显隐、当前激活的 popover 标签 */
const isProgressAnimated = ref(true)
const isRatingDialogOpen = ref(false)
const isRatingDetailLoading = ref(false)
const ratingDetail = ref<RatingDetailPayload | null>(null)
const activeRadarPopoverLabel = ref('')
const displayedRatingScore = ref(getSsrRatingAnimationCurrentScore())
  /** 图表及 popover 容器的 DOM 引用 */
const ratingScoreRef = ref<HTMLElement | null>(null)
const radarChartRef = ref<HTMLElement | null>(null)
const radarHotspotListRef = ref<HTMLElement | null>(null)
  /** 趋势图 DOM 元素及 ECharts 实例注册表 */
const trendChartRefs = new Map<string, HTMLElement>()
const trendChartInstances = new Map<string, ChartProxy>()
let radarChart: ChartProxy | null = null
const radarHotspotRefs = new Map<string, HTMLElement>()
const radarPopoverRefs = new Map<string, HTMLElement>()
const radarPopoverStyles = ref<Record<string, CSSProperties>>({})
const radarPopoverPlacements = ref<Record<string, 'left' | 'right'>>({})
/** 弹窗开启前页面滚动容器的 overflow 样式，用于关闭时原样恢复。 */
let originalBodyOverflow = ''
let originalHtmlOverflow = ''
  /** 雷达图 popover 定位常量 */
const RADAR_POPOVER_GAP = 10
const RADAR_POPOVER_VIEWPORT_PADDING = 8
const OFFICE_VISIT_SWIPE_THRESHOLD = 40
const TREND_PERIOD_VALUES = ['0', '3', '6', '12', '36'] as const
  /** 趋势图展示常量 */
const TREND_CHART_HEIGHT = 208
const TREND_GRID_LINE_COLOR = 'rgba(11, 18, 40, 0.14)'
const TREND_GRID_LINE_WIDTH = 0.5
const TREND_GRID_LINE_LEFT_EXTEND = 4
const TREND_GRID_LINE_TOP_EXTEND = 8
const TREND_GRID_LINE_RIGHT_EXTEND = 20
const TREND_GRID_Y_VALUES = [0, 20, 40, 60, 80, 100]
const TREND_X_GRID_LINE_COUNT = 6
const TREND_INDUSTRY_NAME = 'Industry'
const DEFAULT_TREND_BROKER_NAME = 'FXTM'
const DEFAULT_OFFICE_VISIT_STATUS = 'Perfect'
  /** 办公地实勘轮播状态 */
const activeOfficeVisitIndex = ref(0)
const officeVisitPointerStartX = ref<number | null>(null)
const OFFICE_VISIT_AUTOPLAY_INTERVAL = 2000
let officeVisitAutoplayTimer: ReturnType<typeof window.setInterval> | null = null
  /** 趋势图时间范围选项 */
const trendPeriodOptions = computed(() => {
  const options = (ratingDetail.value?.indexSelectList || [])
    .map((item) => {
      const name = item.name || item.label || item.title || ''
      const rawValue = item.value ?? item.code ?? ''
      const value = String(rawValue)

      if (!name || !value) return null

      return { name, value, checked: !!item.checked }
    })
    .filter((item): item is { name: string, value: string, checked: boolean } => !!item && TREND_PERIOD_VALUES.includes(item.value as typeof TREND_PERIOD_VALUES[number]))

  if (options.length) return options

  return [
    { name: 'All', value: '0', checked: true },
    { name: '3M', value: '3', checked: false },
    { name: '6M', value: '6', checked: false },
    { name: '1Y', value: '12', checked: false },
    { name: '3Y', value: '36', checked: false },
  ]
})
const trendPeriodValueMap = ref<Record<string, string>>({
  active: '',
  opinion: '',
})
const trendChartDataMap = ref<Record<RatingChartType, RatingTrendSeries[]>>({
  active: [],
  opinion: [],
})
const trendChartRequestIdMap = ref<Record<RatingChartType, number>>({
  active: 0,
  opinion: 0,
})
const trendLegendSelectedMap = ref<Record<string, Record<string, boolean>>>({
  active: {
    [TREND_INDUSTRY_NAME]: true,
    [DEFAULT_TREND_BROKER_NAME]: true,
  },
  opinion: {
    [TREND_INDUSTRY_NAME]: true,
    [DEFAULT_TREND_BROKER_NAME]: true,
  },
})

const normalizeRatingNumber = (value?: string | number) => {
  const numberValue = Number(value)
  return Number.isNaN(numberValue) ? emptyText : numberValue.toFixed(2)
}

const isRemoteLottieUrl = (value?: string) => {
  if (!value) return false
  return /^https?:\/\/.+\.json(?:[?#].*)?$/i.test(value.trim())
}

const isValidLottieJson = (value?: string) => {
  if (!value) return false
  if (isRemoteLottieUrl(value)) return true

  try {
    const parsed = JSON.parse(value) as unknown
    return !!parsed && typeof parsed === 'object' && !Array.isArray(parsed)
  } catch {
    return false
  }
}

const getRatingLevelByScore = (score?: string | number, fallback?: string) => {
  if (fallback) return fallback

  const value = Number(score)
  if (Number.isNaN(value)) return emptyText
  if (value >= 6) return 'Danger'
  if (value >= 3) return 'Caution'
  return 'Warning'
}

  /** 格式化后的评分，保留两位小数 */
const ratingScore = computed(() => {
  const value = Number(props.ratingCards?.value)
  if (!Number.isFinite(value)) return emptyText
  return clampRatingScore(value).toFixed(2)
})

const numericRatingScore = computed(() => {
  const value = Number(props.ratingCards?.value)
  if (!Number.isFinite(value)) return 0

  return clampRatingScore(value)
})

/** SSR 输出后立即播放评分数字动画，不等待 Nuxt hydration。 */
useHead({
  script: [
    {
      key: 'exchange-rating-score-ssr-animation',
      tagPosition: 'bodyClose',
      innerHTML: `
        (() => {
          const scoreElement = document.querySelector('[data-ssr-rating-score]')
          if (!scoreElement || scoreElement.dataset.ratingAnimationStarted === 'true') return

          const rawTargetScore = Number(scoreElement.dataset.ratingTarget)
          if (!Number.isFinite(rawTargetScore)) return
          const targetScore = Math.min(Math.max(rawTargetScore, 0), 10)

          const duration = 1500
          const startTime = performance.now()
          const animationState = { currentScore: 0 }
          window.__exchangeRatingScoreAnimation = animationState
          scoreElement.dataset.ratingAnimationStarted = 'true'
          scoreElement.dataset.ratingAnimationStartTime = String(startTime)

          const updateScore = (currentTime) => {
            const progress = Math.min((currentTime - startTime) / duration, 1)
            const easedProgress = 1 - Math.pow(1 - progress, 3)
            const currentScore = Math.max(0, targetScore * easedProgress)
            animationState.currentScore = currentScore
            scoreElement.textContent = currentScore.toFixed(2)
            scoreElement.dataset.ratingAnimationCurrent = String(currentScore)

            if (progress < 1) {
              scoreElement.dataset.ratingAnimationFrame = String(requestAnimationFrame(updateScore))
              return
            }

            scoreElement.dataset.ratingAnimationCompleted = 'true'
            delete scoreElement.dataset.ratingAnimationFrame
          }

          scoreElement.dataset.ratingAnimationFrame = String(requestAnimationFrame(updateScore))
        })()
      `,
    },
  ],
})

let ratingAnimationFrameId: number | null = null

const animatedRatingScore = computed(() => {
  if (ratingScore.value === emptyText) return emptyText
  return displayedRatingScore.value.toFixed(2)
})

const isUltraUltimate = computed(() => Number(props.ultimateType) === 3)

const ratingScoreColor = computed(() => (isUltraUltimate.value ? '#433269' : '#292A2E'))

  /** 进度条可视用的钳位分值（不低于 0.7，不高于 9.5） */
const ratingProgressScore = computed(() => {
  const value = Number(props.ratingCards?.value)
  if (Number.isNaN(value)) return 0

  const normalizedScore = Math.min(Math.max(value, 0), MAX_RATING_SCORE)

  if (normalizedScore > 0 && normalizedScore < MIN_VISIBLE_PROGRESS_SCORE) {
    return MIN_VISIBLE_PROGRESS_SCORE
  }

  if (normalizedScore > MAX_NOTCHED_PROGRESS_SCORE && normalizedScore < MAX_RATING_SCORE) {
    return MAX_NOTCHED_PROGRESS_SCORE
  }

  return normalizedScore
})

  /** 进度条宽度百分比字符串 */
const ratingProgressWidth = computed(() => {
  return `${(ratingProgressScore.value / MAX_RATING_SCORE) * 100}%`
})

const animatedProgressWidth = computed(() => {
  return isProgressAnimated.value ? ratingProgressWidth.value : '0%'
})

  /** 带动画的进度条 CSS 自定义属性 */
const ratingProgressStyle = computed(() => ({
  '--rating-progress-width': animatedProgressWidth.value,
  '--rating-progress-notch-display': animatedProgressWidth.value === '100%' ? 'none' : 'block',
  '--rating-progress-clip-path':
    animatedProgressWidth.value === '100%'
      ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
      : 'polygon(0 0, 100% 0, calc(100% - 8px) 100%, 0 100%)',
}))

  /** 根据评分比例计算进度条颜色 */
const ratingProgressColor = computed(() => {
  if (isUltraUltimate.value) return '#433269'

  const value = Number(props.ratingCards?.value)
  if (Number.isNaN(value)) return '#1868DB'

  const ratio = Math.min(Math.max(value, 0), MAX_RATING_SCORE) / MAX_RATING_SCORE

  if (ratio > 2 / 3) return '#1868DB'
  if (ratio > 1 / 3) return '#F68909'
  return '#E2483D'
})

  /** 本地化后的评分日期字符串 */
const ratingDate = computed(() => {
  return props.ratingCards?.time ? t('83576', [props.ratingCards.time]) : emptyText
})

const dialogRatingDate = computed(() => {
  const time = ratingDetail.value?.main?.dateTime || props.ratingCards?.time
  return time || emptyText
})

const ratingLevel = computed(() => {
  return getRatingLevelByScore(ratingDetail.value?.main?.total || props.ratingCards?.value, ratingDetail.value?.main?.level)
})

const dialogRatingLevelBgColor = computed(() => ratingDetail.value?.main?.levelBgColorIntl || '#e2483d')

const dialogRatingScore = computed(() => normalizeRatingNumber(ratingDetail.value?.main?.total || props.ratingCards?.value))

const ratingExchangeName = computed(() => ratingDetail.value?.main?.name || emptyText)

const ratingExchangeIcon = computed(() => ratingDetail.value?.main?.ico || '')

const getGuardianTableStyle = (bgColors?: string[] | null, borderColors?: string[] | null): CSSProperties | undefined => {
  if (!bgColors?.length && !borderColors?.length) return undefined

  const contentGradient =
    bgColors?.length >= 2
      ? `linear-gradient(180deg, ${bgColors[0]} 0%, ${bgColors[1]} 100%)`
      : 'linear-gradient(180deg, #EAF6F1 0%, #fff 100%)'
  const borderGradient =
    borderColors?.[0]
      ? `linear-gradient(180deg, ${borderColors[0]}54 0%, #fff 60%)`
      : 'linear-gradient(180deg, #77B49854 0%, #fff 60%)'

  return {
    background: `${contentGradient} padding-box, ${borderGradient} border-box`,
  }
}

const normalizeOfficeVisitSlides = (business?: RatingBusinessDetail | RatingBusinessItem | RatingBusinessItem[] | string | null) => {
  const businessItems =
    business && typeof business === 'object' && !Array.isArray(business) && 'items' in business
      ? business.items || []
      : Array.isArray(business)
        ? business
        : business
          ? [business]
          : []

  return businessItems
    .map((item, index) => {
      if (typeof item === 'string') {
        return {
          id: `office-visit-${index + 1}`,
          cover: '',
          date: '',
          label: {
            backgroundColor: '#EAF6F1',
            borderColor: '',
            fontColor: '#1F845A',
            text: DEFAULT_OFFICE_VISIT_STATUS,
          },
          title: item,
          location: '',
        }
      }

      if (!item || typeof item !== 'object') return null

      const firstLabel = item.labels?.[0]

      return {
        id: item.sid || `office-visit-${index + 1}`,
        cover: item.cover || item.icon || '',
        date: '',
        label: {
          backgroundColor: firstLabel?.backgroundColor || '#EAF6F1',
          borderColor: firstLabel?.borderColor || '',
          fontColor: firstLabel?.fontColor || '#1F845A',
          text: firstLabel?.text || item.status || item.level || DEFAULT_OFFICE_VISIT_STATUS,
        },
        title: item.title || item.name || item.content || '',
        location: item.country || '',
      }
    })
    .filter((item): item is { id: string, cover: string, date: string, label: { backgroundColor: string, borderColor: string, fontColor: string, text: string }, title: string, location: string } => {
      return !!item && !!(item.title || item.location || item.cover || item.date)
    })
}

const normalizeAdjustmentItem = (adjustmentItem: RatingAdjustmentItem, index: number) => {
  const adjustment = Number(adjustmentItem.adjustment)
  const adjustmentText = adjustmentItem.adjustmentText || ''
  const trendDirection = Number.isNaN(adjustment) || adjustment >= 0 ? 'up' : 'down'
  const adjustmentDetail = adjustmentItem.adjustmentDetail || EMPTY_ADJUSTMENT_DETAIL
  const deductionList = adjustmentDetail.deductions?.map((deduction) => deduction.content || '').filter(Boolean) || []
  const officeVisitTitle =
    adjustmentDetail.business && typeof adjustmentDetail.business === 'object' && !Array.isArray(adjustmentDetail.business) && 'title' in adjustmentDetail.business
      ? adjustmentDetail.business.title || ''
      : ''
  const guardianTableStyle = getGuardianTableStyle(
    adjustmentDetail.iap?.bgColors || [],
    adjustmentDetail.iap?.borderColors || [],
  )
  const iapItems = adjustmentDetail.iap?.items || []
  const officeVisitSlides = normalizeOfficeVisitSlides(adjustmentDetail.business)
  const guardianItems = iapItems
    .map((iapItem) => ({
      title: iapItem.title || '',
      value: iapItem.value || '',
      valueColor: iapItem.valueColor || '',
    }))
    .filter((iapItem) => iapItem.title || iapItem.value || iapItem.valueColor)

  return {
    key: `adjustment-${index}`,
    hasAdjustmentChange: !Number.isNaN(adjustment) && adjustment !== 0,
    trend: adjustmentText,
    trendStyle: adjustmentItem.adjustmentColor ? { color: adjustmentItem.adjustmentColor } : undefined,
    adjustmentIcon: adjustmentItem.adjustmentIcon || '',
    hasAdjustmentAnimation: isValidLottieJson(adjustmentItem.adjustmentIcon),
    trendClass: trendDirection === 'up' ? 'radar-score-trend-up' : 'radar-score-trend-down',
    adjustmentContent: adjustmentDetail.content || '',
    guardianTitle: adjustmentDetail.iap?.title || '',
    guardianTips: adjustmentDetail.iap?.tips || '',
    guardianAmount: adjustmentDetail.iap?.amount || '',
    guardianAmountIcon: adjustmentDetail.iap?.amountIcon || '',
    guardianItems,
    guardianTableStyle,
    officeVisitTitle,
    officeVisitSlides,
    deductions: officeVisitSlides.length ? [] : deductionList,
  }
}

const normalizeSmallScore = (item: RatingSmallScore) => {
  const adjustment = Number(item.adjustment)
  const adjustmentText = item.adjustmentText || ''
  const trendDirection = Number.isNaN(adjustment) || adjustment >= 0 ? 'up' : 'down'
  const adjustmentDetails = item.adjustments?.length
    ? item.adjustments
    : item.adjustmentDetail
      ? [{
        adjustment: item.adjustment,
        adjustmentColor: item.adjustmentColor,
        adjustmentDetail: item.adjustmentDetail,
        adjustmentIcon: item.adjustmentIcon,
        adjustmentText: item.adjustmentText,
      }]
      : []

  return {
    label: item.name || emptyText,
    value: normalizeRatingNumber(item.total),
    totalValue: Number.isNaN(Number(item.total)) ? 0 : Number(item.total),
    adjustmentValue: Number.isNaN(adjustment) ? 0 : adjustment,
    level: getRatingLevelByScore(item.total, item.level),
    levelStyle: { backgroundColor: item.bgColor},
    hasAdjustmentChange: !Number.isNaN(adjustment) && adjustment !== 0,
    trend: adjustmentText,
    trendStyle: item.adjustmentColor ? { color: item.adjustmentColor } : undefined,
    adjustmentIcon: item.adjustmentIcon || '',
    hasAdjustmentAnimation: isValidLottieJson(item.adjustmentIcon),
    trendClass: trendDirection === 'up' ? 'radar-score-trend-up' : 'radar-score-trend-down',
    trendDirection,
    description: item.desc || '',
    adjustments: adjustmentDetails.map(normalizeAdjustmentItem),
  }
}

  /** 五维雷达图指标定义（含 popover 数据） */
const ratingMetrics = computed(() => {
  const apiScores = ratingDetail.value?.main?.smallScores || []

  if (apiScores.length) {
    return apiScores.map(normalizeSmallScore)
  }

  return []
})


const radarScoreHotspots = computed(() => {
  return ratingMetrics.value.map((item, index) => ({
    ...item,
    className: `radar-score-hotspot-${index}`,
  }))
})

  /** 从评分指标提取的雷达图数值序列 */
const radarData = computed(() => {
  return ratingMetrics.value.map((item) => {
    const value = Number(item.value)
    if (Number.isNaN(value)) return 0

    const normalizedScore = Math.min(Math.max(value, 0), MAX_RATING_SCORE)

    if (normalizedScore <= RADAR_LOW_SCORE_THRESHOLD) {
      return (
        MIN_VISIBLE_RADAR_SCORE
        + (normalizedScore / RADAR_LOW_SCORE_THRESHOLD) * (RADAR_LOW_SCORE_THRESHOLD - MIN_VISIBLE_RADAR_SCORE)
      )
    }

    return (
      RADAR_LOW_SCORE_THRESHOLD
      + ((normalizedScore - RADAR_LOW_SCORE_THRESHOLD) / (MAX_RATING_SCORE - RADAR_LOW_SCORE_THRESHOLD))
        * (MAX_RATING_SCORE - RADAR_LOW_SCORE_THRESHOLD)
    )
  })
})

  /** 雷达图坐标轴指标定义 */
const radarIndicators = computed(() => {
  return ratingMetrics.value.map((item) => ({
    name: item.label,
    max: MAX_RATING_SCORE,
    min: 0,
    value: item.value,
  }))
})

const hasRadarMetrics = computed(() => radarIndicators.value.length >= 3 && radarData.value.length === radarIndicators.value.length)

  /** 构建 ECharts 雷达图配置 */
const getRadarOption = (): EChartsOption => {
  const radarBackground = [
    'rgba(24, 104, 219, 0.22)',
    'rgba(24, 104, 219, 0.17)',
    'rgba(24, 104, 219, 0.13)',
    'rgba(24, 104, 219, 0.09)',
    'rgba(24, 104, 219, 0.05)',
  ]
  const radarLineColor = 'rgba(70, 136, 236, 0.28)'
  const radarSplitLineColor = 'rgba(70, 136, 236, 0.22)'
  const radarAreaColor = 'rgba(59, 118, 246, 1)'
  const radarPointColor = '#ffffff'

  return {
    color: ['rgba(36, 104, 222, 0.32)'],
    radar: {
      indicator: radarIndicators.value,
      center: ['50%', '51%'],
      radius: 93,
      startAngle: 90,
      clockwise: true,
      splitNumber: 5,
      silent: true,
      splitArea: {
        areaStyle: {
          color: radarBackground,
        },
      },
      axisLine: {
        lineStyle: {
          color: radarLineColor,
          type: 'dashed',
          width: 1,
        },
      },
      splitLine: {
        lineStyle: {
          color: radarSplitLineColor,
          type: 'solid',
          width: 1,
        },
      },
      axisName: {
        color: '#fff',
        distance: 15,
        rich: {
          label: {
            color: '#fff',
            fontSize: 12,
            lineHeight: 20,
            align: 'center',
          },
          value: {
            color: '#fff',
            fontFamily: 'Outfit',
            fontSize: 16,
            fontWeight: 600,
            lineHeight: 20,
            align: 'center',
          },
        },
        formatter: () => {
          return ''
        },
      },
    },
    series: [
      {
        type: 'radar',
        emphasis: {
          disabled: true,
        },
        symbol: `image://${RADAR_CENTER_MARKER_IMAGE}`,
        symbolSize: [8, 8],
        itemStyle: {
          color: radarPointColor,
          borderColor: 'rgba(36, 104, 222, 0.72)',
          borderWidth: 1.5,
        },
        lineStyle: {
          width: 1,
          color: 'rgba(36, 104, 222, 0.92)',
        },
        data: [
          {
            value: radarData.value,
            areaStyle: {
              color: radarAreaColor,
            },
            symbol: `image://${RADAR_CENTER_MARKER_IMAGE}`,
            symbolSize: [8, 8],
            itemStyle: {
              color: radarPointColor,
              borderColor: 'rgba(36, 104, 222, 0.72)',
              borderWidth: 1.5,
            },
            lineStyle: {
              width: 1,
              color: 'rgba(36, 104, 222, 0.92)',
            },
          },
        ],
      },
    ],
  }
}

type TrendChartCard = {
  key: string
  title: string
  showArea: boolean
  series: RatingTrendSeries[]
}

const trendChartCards = computed<TrendChartCard[]>(() => {
  return [
    {
      key: 'active',
      title: t('84469'),
      showArea: false,
      series: trendChartDataMap.value.active || [],
    },
    {
      key: 'opinion',
      title: t('84467'),
      showArea: false,
      series: trendChartDataMap.value.opinion || [],
    },
  ].filter((card) => card.series.length)
})

const getTrendBrokerName = (card: TrendChartCard) =>
  card.series.find((item) => !item.isIndustry)?.name || DEFAULT_TREND_BROKER_NAME

const getTrendIndustryName = (card: TrendChartCard) =>
  card.series.find((item) => item.isIndustry)?.name || TREND_INDUSTRY_NAME

const getTrendLegendItems = (card: TrendChartCard) =>
  card.series
    .filter((item) => item.name?.trim())
    .map((item, index) => ({
      name: index === 0 ? (ratingExchangeName.value === emptyText ? '' : ratingExchangeName.value) : item.name?.trim() || '',
      className: item.isIndustry ? 'rating-dialog-legend-orange' : 'rating-dialog-legend-blue',
    }))
    .filter((item) => item.name)

const getTrendSeriesMonths = (card: TrendChartCard) => {
  const months = new Set<string>()
  card.series.forEach((series) => {
    series.items?.forEach((item) => {
      if (item.month) months.add(item.month)
    })
  })

  return [...months].toSorted()
}

const getTrendCardPeriod = (key: string) => trendPeriodValueMap.value[key] || trendPeriodOptions.value[0]?.value || ''

const getTrendLegendSelected = (card: TrendChartCard) => ({
  [getTrendIndustryName(card)]: trendLegendSelectedMap.value[card.key]?.[getTrendIndustryName(card)] ?? true,
  [getTrendBrokerName(card)]: trendLegendSelectedMap.value[card.key]?.[getTrendBrokerName(card)] ?? true,
})

const isTrendLegendSelected = (key: string, name: string) => trendLegendSelectedMap.value[key]?.[name] ?? true

const getTrendSeriesByPeriod = (data: number[], period: string) => {
  const count = Number(period)
  if (!Number.isFinite(count) || count <= 0 || count >= data.length) return data
  return data.slice(-count)
}

const getTrendMonthsByPeriod = (card: TrendChartCard, period: string) => {
  const months = getTrendSeriesMonths(card)
  const count = Number(period)
  if (!Number.isFinite(count) || count <= 0 || count >= months.length) return months
  return months.slice(-count)
}

const getTrendDefaultPeriodValue = () => {
  const checkedOption = trendPeriodOptions.value.find((item) => item.checked)
  return checkedOption?.value || trendPeriodOptions.value[0]?.value || ''
}

const normalizeTrendPeriodValue = (value: string, options: { value: string }[]) => {
  if (!value) return getTrendDefaultPeriodValue()
  return options.some((item) => item.value === value) ? value : getTrendDefaultPeriodValue()
}

const getTrendSeriesValuesByPeriod = (series: RatingTrendSeries | undefined, months: string[]) => {
  const valueMap = new Map((series?.items || []).map((item) => [item.month, Number(item.value)]))

  return months.map((month) => {
    const value = valueMap.get(month)
    return Number.isFinite(value) ? Number(value) : 0
  })
}

const getTrendXAxisTickIndexes = (months: string[]) => {
  if (months.length <= TREND_X_GRID_LINE_COUNT) return months.map((_, index) => index)

  const lastIndex = months.length - 1
  const indexes = Array.from({ length: TREND_X_GRID_LINE_COUNT }, (_, index) =>
    Math.round((index * lastIndex) / (TREND_X_GRID_LINE_COUNT - 1)),
  )

  return [...new Set(indexes)]
}

  /** 构建趋势卡片对应的 ECharts 折线图配置 */
const getTrendChartOption = (card: TrendChartCard): EChartsOption => {
  const period = getTrendCardPeriod(card.key)
  const industrySeries = card.series.find((item) => item.isIndustry) || card.series[0]
  const brokerSeries = card.series.find((item) => !item.isIndustry) || card.series[1]
  const industryName = industrySeries?.name || TREND_INDUSTRY_NAME
  const brokerName = brokerSeries?.name || DEFAULT_TREND_BROKER_NAME
  const months = getTrendMonthsByPeriod(card, period)
  const xAxisTickIndexes = new Set(getTrendXAxisTickIndexes(months))
  const areaStyle1 = card.showArea
    ? {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(238, 170, 80, 0.2)' },
            { offset: 1, color: 'rgba(238, 170, 80, 0)' },
          ],
        },
      }
    : { color: 'rgba(238, 170, 80, 0)' }
  const areaStyle2 = card.showArea
    ? {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(76, 129, 244, 0.2)' },
            { offset: 1, color: 'rgba(76, 129, 244, 0)' },
          ],
        },
      }
    : { color: 'rgba(76, 129, 244, 0)' }

  const grid = { top: 10, left: 16, right: 24, bottom: 54, containLabel: true }

  return {
    animation: true,
    animationDuration: 1000,
    grid,
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#F68909',
        },
      },
      appendToBody: true,
      backgroundColor: '#292A2E',
      borderWidth: 0,
      padding: 0,
      extraCssText:
        'z-index: 3100 !important; border-radius: 4px; box-shadow: 0 8px 8px rgba(0, 0, 0, 0.1);',
      formatter(params) {
        const list = Array.isArray(params) ? params : []
        const first = list[0]
        const second = list[1]
        const markerStyle =
          'display:inline-block; width:6px; height:2px; border-radius:45px; flex:0 0 6px;'
        const valueStyle =
          'color:rgba(255,255,255,0.6); font-size:12px; font-weight:400; line-height:18px; white-space:nowrap;'

        return `
          <div style="display:flex; flex-direction:column; align-items:flex-start; justify-content:center; gap:4px; padding:4px 8px; background:#292A2E; border-radius:4px;">
            <div style="color:#FFFFFF; font-size:14px; font-weight:500; line-height:22px; white-space:nowrap;">
              ${first?.name || ''}
            </div>
            <div style="display:flex; flex-direction:column; align-items:flex-start;">
              <div style="display:flex; align-items:center; gap:4px;">
                <span style="${markerStyle} background:#F68909;"></span>
                <span style="${valueStyle}">${Number(first?.data || 0).toFixed(0)}</span>
              </div>
              <div style="display:flex; align-items:center; gap:4px;">
                <span style="${markerStyle} background:#4688EC;"></span>
                <span style="${valueStyle}">${Number(second?.data || 0).toFixed(0)}</span>
              </div>
            </div>
          </div>
        `
      },
    },
    legend: {
      show: false,
      selected: getTrendLegendSelected(card),
    },
    color: ['#EFA33F', '#0081FF'],
    xAxis: {
      data: months,
      type: 'category',
      inverse: false,
      boundaryGap: false,
      axisLine: { show: false },
      axisLabel: {
        color: '#7D818A',
        fontSize: 10,
        margin: 16,
        interval: (index: number) => xAxisTickIndexes.has(index),
      },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      max: 100,
      interval: 20,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#7D818A',
        fontSize: 10,
        formatter: (value: number) => Number(value).toFixed(1),
      },
      splitLine: {
        show: true,
        lineStyle: { color: TREND_GRID_LINE_COLOR, type: 'dashed', width: TREND_GRID_LINE_WIDTH },
      },
    },
    series: [
      {
        name: industryName,
        data: getTrendSeriesByPeriod(getTrendSeriesValuesByPeriod(industrySeries, months), period),
        type: 'line',
        itemStyle: { color: '#EFA33F' },
        smooth: true,
        symbol: 'none',
        areaStyle: areaStyle1,
      },
      {
        name: brokerName,
        data: getTrendSeriesByPeriod(getTrendSeriesValuesByPeriod(brokerSeries, months), period),
        type: 'line',
        itemStyle: { color: '#0081FF' },
        smooth: true,
        symbol: 'none',
        areaStyle: areaStyle2,
      },
    ],
  }
}

const getTrendVerticalGridGraphic = (chart: Pick<ChartProxy, 'convertToPixel'>, card: TrendChartCard) => {
  const period = getTrendCardPeriod(card.key)
  const months = getTrendMonthsByPeriod(card, period)
  const topY = Number(chart.convertToPixel({ yAxisIndex: 0 }, 100))
  const bottomY = Number(chart.convertToPixel({ yAxisIndex: 0 }, 0))

  if (![topY, bottomY].every(Number.isFinite)) return []

  return getTrendXAxisTickIndexes(months)
    .map((index) => {
      const month = months[index]
      if (!month) return null

      const x = Number(chart.convertToPixel({ xAxisIndex: 0 }, month))
      if (!Number.isFinite(x)) return null

      return {
        type: 'line',
        silent: true,
        z: 0,
        shape: {
          x1: x,
          y1: topY - TREND_GRID_LINE_TOP_EXTEND,
          x2: x,
          y2: bottomY,
        },
        style: {
          stroke: TREND_GRID_LINE_COLOR,
          lineDash: [4, 4],
          lineWidth: TREND_GRID_LINE_WIDTH,
        },
      }
    })
    .filter((item): item is NonNullable<typeof item> => !!item)
}

const getTrendHorizontalExtensionGraphic = (chart: Pick<ChartProxy, 'convertToPixel'>, card: TrendChartCard) => {
  const period = getTrendCardPeriod(card.key)
  const months = getTrendMonthsByPeriod(card, period)
  const firstMonth = months[0]
  const lastMonth = months[months.length - 1]

  if (!firstMonth || !lastMonth) return []

  const leftX = Number(chart.convertToPixel({ xAxisIndex: 0 }, firstMonth))
  const rightX = Number(chart.convertToPixel({ xAxisIndex: 0 }, lastMonth))

  if (![leftX, rightX].every(Number.isFinite)) return []

  return TREND_GRID_Y_VALUES
    .map((value) => {
      const y = Number(chart.convertToPixel({ yAxisIndex: 0 }, value))
      if (!Number.isFinite(y)) return []

      return [
        {
          type: 'line',
          silent: true,
          z: 0,
          shape: {
            x1: leftX - TREND_GRID_LINE_LEFT_EXTEND,
            y1: y,
            x2: leftX,
            y2: y,
          },
          style: {
            stroke: TREND_GRID_LINE_COLOR,
            lineDash: [4, 4],
            lineWidth: TREND_GRID_LINE_WIDTH,
          },
        },
        {
          type: 'line',
          silent: true,
          z: 0,
          shape: {
            x1: rightX,
            y1: y,
            x2: rightX + TREND_GRID_LINE_RIGHT_EXTEND,
            y2: y,
          },
          style: {
            stroke: TREND_GRID_LINE_COLOR,
            lineDash: [4, 4],
            lineWidth: TREND_GRID_LINE_WIDTH,
          },
        },
      ]
    })
    .flat()
}

const getTrendGridGraphic = (chart: Pick<ChartProxy, 'convertToPixel'>, card: TrendChartCard) => [
  ...getTrendVerticalGridGraphic(chart, card),
  ...getTrendHorizontalExtensionGraphic(chart, card),
]

const setTrendChartRef = (key: string, el: Element | null) => {
  const previousChart = trendChartInstances.get(key)

  if (el instanceof HTMLElement) {
    trendChartRefs.set(key, el)
    return
  }

  trendChartRefs.delete(key)
  if (previousChart) {
    previousChart.dispose()
    trendChartInstances.delete(key)
  }
}

  /** 初始化所有趋势图 ECharts 实例 */
const renderTrendCharts = async () => {
  if (!import.meta.client) return

  await nextTick()

  await Promise.all(trendChartCards.value.map(async (card) => {
    const element = trendChartRefs.get(card.key)
    if (!element) return

    element.style.height = `${TREND_CHART_HEIGHT}px`

    const chart = trendChartInstances.get(card.key) || init(element, { height: TREND_CHART_HEIGHT })
    if (!chart) return

    trendChartInstances.set(card.key, chart)
    const instance = await chart.ready()
    if (!instance || trendChartInstances.get(card.key) !== chart) return

    chart.resize({ height: TREND_CHART_HEIGHT })
    /** 清除旧折线的更新过渡，确保每次都从横轴左侧重新播放入场动画。 */
    chart.clear()

    /** 自定义网格需要在坐标系生成后计算，等待折线动画结束再追加，避免中断动画。 */
    const handleAnimationFinished = () => {
      chart.off('finished', handleAnimationFinished)
      if (trendChartInstances.get(card.key) !== chart) return
      chart.setOption({ graphic: getTrendGridGraphic(chart, card) })
    }

    chart.on('finished', handleAnimationFinished)
    chart.setOption(getTrendChartOption(card), { notMerge: true })
  }))
}

const resizeTrendCharts = () => {
  trendChartInstances.forEach((chart, key) => {
    const card = trendChartCards.value.find((item) => item.key === key)

    chart.resize({ height: TREND_CHART_HEIGHT })
    if (card) {
      chart.setOption(getTrendChartOption(card), { notMerge: true })
      chart.setOption({ graphic: getTrendGridGraphic(chart, card) })
    }
  })
}

const disposeTrendCharts = () => {
  trendChartInstances.forEach((chart) => chart.dispose())
  trendChartInstances.clear()
}

const updateTrendPeriod = (key: string, value: string) => {
  trendPeriodValueMap.value = {
    ...trendPeriodValueMap.value,
    [key]: value,
  }
}

const toggleTrendLegend = (key: string, name: string) => {
  const selected = getTrendLegendSelected(key)
  const nextSelected = {
    ...selected,
    [name]: !selected[name],
  }

  trendLegendSelectedMap.value = {
    ...trendLegendSelectedMap.value,
    [key]: nextSelected,
  }

  trendChartInstances.get(key)?.setOption({
    legend: {
      selected: nextSelected,
    },
  })
}

const officeVisitTrackStyle = computed(() => ({
  transform: `translateX(-${activeOfficeVisitIndex.value * 100}%)`,
}))

const toOfficeVisitSlide = (index: number, total: number) => {
  if (!total) return
  activeOfficeVisitIndex.value = (index + total) % total
}

const moveOfficeVisitSlide = (step: -1 | 1, total: number) => {
  toOfficeVisitSlide(activeOfficeVisitIndex.value + step, total)
}

const handleOfficeVisitPointerDown = (event: PointerEvent) => {
  officeVisitPointerStartX.value = event.clientX
  if (event.currentTarget instanceof HTMLElement) {
    event.currentTarget.setPointerCapture(event.pointerId)
  }
}

const handleOfficeVisitPointerUp = (event: PointerEvent, total: number) => {
  if (officeVisitPointerStartX.value === null) return

  const offsetX = event.clientX - officeVisitPointerStartX.value
  officeVisitPointerStartX.value = null

  if (Math.abs(offsetX) < OFFICE_VISIT_SWIPE_THRESHOLD) return

  moveOfficeVisitSlide(offsetX > 0 ? -1 : 1, total)
}

const handleOfficeVisitPointerCancel = () => {
  officeVisitPointerStartX.value = null
}

const stopOfficeVisitAutoplay = () => {
  if (officeVisitAutoplayTimer === null || !import.meta.client) return
  window.clearInterval(officeVisitAutoplayTimer)
  officeVisitAutoplayTimer = null
}

const startOfficeVisitAutoplay = () => {
  if (!import.meta.client || officeVisitAutoplayTimer !== null) return

  officeVisitAutoplayTimer = window.setInterval(() => {
    const track = document.querySelector<HTMLElement>('.radar-score-hotspot-active .radar-score-popover-office-track')
    const total = track?.children.length || 0
    if (total > 1) moveOfficeVisitSlide(1, total)
  }, OFFICE_VISIT_AUTOPLAY_INTERVAL)
}

const syncTrendPeriodValues = () => {
  const options = trendPeriodOptions.value
  const defaultValue = getTrendDefaultPeriodValue()
  if (!defaultValue) return

  trendPeriodValueMap.value = {
    active: normalizeTrendPeriodValue(trendPeriodValueMap.value.active, options),
    opinion: normalizeTrendPeriodValue(trendPeriodValueMap.value.opinion, options),
  }
}

const loadTrendChart = async (type: RatingChartType) => {
  if (!props.exchangeId) {
    trendChartDataMap.value = {
      ...trendChartDataMap.value,
      [type]: [],
    }
    return
  }

  const requestId = trendChartRequestIdMap.value[type] + 1
  trendChartRequestIdMap.value = {
    ...trendChartRequestIdMap.value,
    [type]: requestId,
  }

  try {
    const response = await getRatingChart({
      exchangeId: props.exchangeId,
      type,
      range: Number(getTrendCardPeriod(type) || 0),
    })

    if (trendChartRequestIdMap.value[type] !== requestId) return

    trendChartDataMap.value = {
      ...trendChartDataMap.value,
      [type]: response?.series || [],
    }
  } catch {
    if (trendChartRequestIdMap.value[type] !== requestId) return

    trendChartDataMap.value = {
      ...trendChartDataMap.value,
      [type]: [],
    }
  }
}

const loadRatingDetail = async () => {
  if (!props.exchangeId || ratingDetail.value || isRatingDetailLoading.value) return

  isRatingDetailLoading.value = true
  try {
    ratingDetail.value = await getRatingDetail(props.exchangeId)
    // console.log(props.exchangeId,"props.exchangeId")
    // console.log(ratingDetail.value,"ratingDetail")
    syncTrendPeriodValues()
  } catch {
    ratingDetail.value = null
  } finally {
    isRatingDetailLoading.value = false
  }
}

const matomo = useMatomo()


  /** 打开评分详情弹窗 */
const openRatingDialog = async () => {
  matomo.trackEvent('broker_detail_left', {
    type_click: '天眼评分',
    name: props.exchangeName || '',
    code: props.exchangeId || '',
  })

  // 首次打开前先准备详情数据，避免弹窗先以空数据挂载后再次更新造成内容闪动。
  await loadRatingDetail()
  isRatingDialogOpen.value = true

  if (ratingDetail.value) {
    syncTrendPeriodValues()
    void Promise.all([
      loadTrendChart('active'),
      loadTrendChart('opinion'),
    ])
  }
}

  /** 关闭评分详情弹窗并清理资源 */
const closeRatingDialog = () => {
  isRatingDialogOpen.value = false
  activeRadarPopoverLabel.value = ''
}

const openIapDialogFromRatingDialog = () => {
  closeRatingDialog()
  emit('open-iap')
}

const setRadarHotspotRef = (label: string, el: Element | null) => {
  if (el instanceof HTMLElement) {
    radarHotspotRefs.set(label, el)
    return
  }

  radarHotspotRefs.delete(label)
}

const setRadarPopoverRef = (label: string, el: Element | null) => {
  if (el instanceof HTMLElement) {
    radarPopoverRefs.set(label, el)
    return
  }

  radarPopoverRefs.delete(label)
}

const getRadarPopoverStyle = (label: string) => {
  return radarPopoverStyles.value[label] || {}
}

const getRadarPopoverPlacement = (label: string) => {
  return radarPopoverPlacements.value[label] || 'right'
}

const getRadarTrackWidth = (value?: string | number) => {
  const score = Number(value)

  if (Number.isNaN(score)) return '0%'

  return `${(Math.min(Math.max(score, 0), MAX_RATING_SCORE) / MAX_RATING_SCORE) * 100}%`
}

const getRadarTrackRestStyle = (total?: string | number, adjustment?: string | number) => {
  const totalValue = Number(total)
  const adjustmentValue = Number(adjustment)
  const isPositiveAdjustment = !Number.isNaN(adjustmentValue) && adjustmentValue > 0
  const isNegativeAdjustment = !Number.isNaN(adjustmentValue) && adjustmentValue < 0

  let widthValue = totalValue

  if (isNegativeAdjustment) {
    widthValue = totalValue - adjustmentValue
  } else if (isPositiveAdjustment) {
    widthValue = totalValue
  }

  return {
    '--radar-score-track-rest-width': getRadarTrackWidth(widthValue),
    '--radar-score-track-rest-sync-width': getRadarTrackWidth(totalValue - adjustmentValue),
    '--radar-score-track-rest-color': isPositiveAdjustment ? '#1F845A' : '#DCDDE0',
    '--radar-score-track-rest-border-color': isPositiveAdjustment ? 'rgba(31, 132, 90, 0.18)' : 'rgba(11, 18, 40, 0.14)',
  }
}

const getRadarTrackFillStyle = (total?: string | number, adjustment?: string | number) => {
  const totalValue = Number(total)
  const adjustmentValue = Number(adjustment)
  const isPositiveAdjustment = !Number.isNaN(adjustmentValue) && adjustmentValue > 0
  const isNegativeAdjustment = !Number.isNaN(adjustmentValue) && adjustmentValue < 0

  let widthValue = totalValue

  if (isPositiveAdjustment) {
    widthValue = totalValue - adjustmentValue
  } else if (isNegativeAdjustment) {
    widthValue = totalValue
  }

  return {
    '--radar-score-track-fill-width': getRadarTrackWidth(widthValue),
  }
}

const updateRadarPopoverPosition = async (label: string) => {
  if (!import.meta.client) return

  await nextTick()

  const hotspot = radarHotspotRefs.get(label)
  const popover = radarPopoverRefs.get(label)

  if (!hotspot || !popover) return

  const hotspotRect = hotspot.getBoundingClientRect()
  const popoverRect = popover.getBoundingClientRect()
  const isRtl = document.documentElement.dir === 'rtl'

  const pointX = hotspotRect.width / 2
  const pointY = hotspotRect.height / 2
  const boxWidth = popoverRect.width
  const boxHeight = popoverRect.height
  const viewportWidth = document.documentElement.clientWidth
  const viewportHeight = document.documentElement.clientHeight
  const availableLeft = hotspotRect.left - RADAR_POPOVER_VIEWPORT_PADDING
  const availableRight = viewportWidth - hotspotRect.right - RADAR_POPOVER_VIEWPORT_PADDING

  const placement = availableLeft >= boxWidth || availableLeft > availableRight ? 'left' : 'right'
  const rawHorizontalOffset = placement === 'right' ? hotspotRect.width + RADAR_POPOVER_GAP : -boxWidth - RADAR_POPOVER_GAP
  const minHorizontalOffset = RADAR_POPOVER_VIEWPORT_PADDING - hotspotRect.left
  const maxHorizontalOffset = viewportWidth - RADAR_POPOVER_VIEWPORT_PADDING - hotspotRect.left - boxWidth
  const horizontalOffset = Math.min(Math.max(rawHorizontalOffset, minHorizontalOffset), maxHorizontalOffset)

  const minTop = RADAR_POPOVER_VIEWPORT_PADDING - hotspotRect.top
  const maxTop = viewportHeight - RADAR_POPOVER_VIEWPORT_PADDING - hotspotRect.top - boxHeight
  const alignTop = 0
  const alignBottom = hotspotRect.height - boxHeight
  const alignCenter = pointY - boxHeight / 2
  const canAlignTop = alignTop >= minTop && alignTop <= maxTop
  const canAlignBottom = alignBottom >= minTop && alignBottom <= maxTop
  const rawTop = canAlignTop ? alignTop : canAlignBottom ? alignBottom : alignCenter
  const top = Math.min(Math.max(rawTop, minTop), maxTop)

  const arrowTop = Math.min(Math.max(pointY - top - 5, 14), boxHeight - 18)

  radarPopoverStyles.value = {
    ...radarPopoverStyles.value,
    [label]: {
      left: isRtl ? 'auto' : `${horizontalOffset}px`,
      right: isRtl ? `${horizontalOffset}px` : 'auto',
      top: `${top}px`,
      '--radar-popover-arrow-top': `${arrowTop}px`,
    },
  }
  radarPopoverPlacements.value = {
    ...radarPopoverPlacements.value,
    [label]: placement,
  }
}

  /** hover/focus 新指标时关闭已点击打开的其它 popover，并预计算当前位置 */
const prepareRadarPopover = (label: string) => {
  if (activeRadarPopoverLabel.value && activeRadarPopoverLabel.value !== label) {
    activeRadarPopoverLabel.value = ''
  }

  void updateRadarPopoverPosition(label)
}

  /** 切换雷达图 popover 显隐状态 */
const toggleRadarPopover = (label: string) => {
  if (activeRadarPopoverLabel.value === label) return
  activeRadarPopoverLabel.value = label
}

  /** 关闭当前激活的雷达图 popover */
const closeRadarPopover = () => {
  activeRadarPopoverLabel.value = ''
}

const handleDocumentClick = (event: MouseEvent) => {
  const target = event.target as HTMLElement | null

  if (!target) return

  if (target.closest('.radar-score-popover') || target.closest('.radar-score-hotspot')) {
    return
  }

  closeRadarPopover()
}

const resizeRadarChart = () => {
  radarChart?.resize()
  resizeTrendCharts()
  if (activeRadarPopoverLabel.value) {
    void updateRadarPopoverPosition(activeRadarPopoverLabel.value)
  }
}

  /** 初始化雷达图 ECharts 实例 */
const renderRadarChart = async () => {
  if (!import.meta.client) return

  await nextTick()

  if (!radarChartRef.value || !hasRadarMetrics.value) {
    disposeRadarChart()
    return
  }

  const chart = radarChart || init(radarChartRef.value)
  if (!chart) return

  radarChart = chart
  chart.setOption(getRadarOption(), { notMerge: true })
  resizeRadarChart()
}

const disposeRadarChart = () => {
  if (!radarChart) return
  radarChart.dispose()
  radarChart = null
}

/** 锁定或恢复蒙层后的页面滚动。 */
const togglePageScrollLock = (locked: boolean) => {
  if (!import.meta.client) return

  if (locked) {
    originalBodyOverflow = document.body.style.overflow
    originalHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    return
  }

  document.body.style.overflow = originalBodyOverflow
  document.documentElement.style.overflow = originalHtmlOverflow
}

watch(isRatingDialogOpen, (value) => {
  if (!import.meta.client) return
  togglePageScrollLock(value)

  if (value) {
    renderRadarChart()
    renderTrendCharts()
    window.addEventListener('resize', resizeRadarChart)
    return
  }

  window.removeEventListener('resize', resizeRadarChart)
  disposeRadarChart()
  disposeTrendCharts()
})

watch([radarData, radarIndicators], () => {
  if (!isRatingDialogOpen.value) return
  if (!hasRadarMetrics.value) {
    disposeRadarChart()
    return
  }
  if (!radarChart) return
  radarChart.setOption(getRadarOption(), true)
})

watch(() => trendPeriodValueMap.value.active, (value, oldValue) => {
  if (!isRatingDialogOpen.value || !props.exchangeId || !value || value === oldValue) return
  void loadTrendChart('active')
})

watch(() => trendPeriodValueMap.value.opinion, (value, oldValue) => {
  if (!isRatingDialogOpen.value || !props.exchangeId || !value || value === oldValue) return
  void loadTrendChart('opinion')
})

watch(trendPeriodOptions, (options) => {
  const defaultValue = getTrendDefaultPeriodValue()
  if (!defaultValue) return

  trendPeriodValueMap.value = {
    active: normalizeTrendPeriodValue(trendPeriodValueMap.value.active, options),
    opinion: normalizeTrendPeriodValue(trendPeriodValueMap.value.opinion, options),
  }
})

watch(ratingDetail, () => {
  if (!isRatingDialogOpen.value) return
  renderRadarChart()
  renderTrendCharts()
})

watch(trendChartDataMap, () => {
  if (!isRatingDialogOpen.value) return
  renderTrendCharts()
}, { deep: true })

watch(activeRadarPopoverLabel, (value) => {
  activeOfficeVisitIndex.value = 0
  stopOfficeVisitAutoplay()

  if (!import.meta.client) return

  if (value) {
    startOfficeVisitAutoplay()
    document.addEventListener('click', handleDocumentClick)
    void updateRadarPopoverPosition(value)
    return
  }

  document.removeEventListener('click', handleDocumentClick)
})

watch(numericRatingScore, (value) => {
  if (value === displayedRatingScore.value) return

  animateRatingScore(value)
  if (value > 0) {
    isProgressAnimated.value = true
  }
}, { immediate: false })

onMounted(() => {
  const scoreElement = ratingScoreRef.value
  const ssrAnimationFrameId = Number(scoreElement?.dataset.ratingAnimationFrame)
  const ssrCurrentScore = getSsrRatingAnimationCurrentScore()
  const hasCompletedSsrAnimation = scoreElement?.dataset.ratingAnimationCompleted === 'true'
  const ssrStartTime = Number(scoreElement?.dataset.ratingAnimationStartTime)

  if (scoreElement?.dataset.ratingAnimationStarted === 'true') {
    if (Number.isInteger(ssrAnimationFrameId)) {
      window.cancelAnimationFrame(ssrAnimationFrameId)
    }
    if (Number.isFinite(ssrCurrentScore)) {
      displayedRatingScore.value = clampRatingScore(ssrCurrentScore)
    }
    if (hasCompletedSsrAnimation) {
      displayedRatingScore.value = numericRatingScore.value
    } else {
      animateRatingScore(numericRatingScore.value, ssrStartTime)
    }
  } else {
    animateRatingScore(numericRatingScore.value)
  }
  isProgressAnimated.value = true
})

onBeforeUnmount(() => {
  cancelRatingScoreAnimation()
  stopOfficeVisitAutoplay()
  if (import.meta.client) {
    togglePageScrollLock(false)
    window.removeEventListener('resize', resizeRadarChart)
    document.removeEventListener('click', handleDocumentClick)
  }
  disposeRadarChart()
  disposeTrendCharts()
})

const cancelRatingScoreAnimation = () => {
  if (ratingAnimationFrameId === null || !import.meta.client) return
  window.cancelAnimationFrame(ratingAnimationFrameId)
  ratingAnimationFrameId = null
}

const animateRatingScore = (targetValue: number, originalStartTime?: number) => {
  if (!import.meta.client) {
    displayedRatingScore.value = clampRatingScore(targetValue)
    return
  }

  cancelRatingScoreAnimation()

  const hasOriginalStartTime = typeof originalStartTime === 'number' && Number.isFinite(originalStartTime)
  const startValue = hasOriginalStartTime ? 0 : clampRatingScore(displayedRatingScore.value)
  const normalizedTargetValue = clampRatingScore(targetValue)
  const startTime = hasOriginalStartTime ? originalStartTime as number : window.performance.now()
  const duration = RATING_ANIMATION_DURATION

  const update = (currentTime: number) => {
    const progress = Math.min((currentTime - startTime) / duration, 1)
    const easedProgress = 1 - Math.pow(1 - progress, 3)
    displayedRatingScore.value = clampRatingScore(startValue + (normalizedTargetValue - startValue) * easedProgress)

    if (progress < 1) {
      ratingAnimationFrameId = window.requestAnimationFrame(update)
      return
    }

    displayedRatingScore.value = normalizedTargetValue
    ratingAnimationFrameId = null
  }

  ratingAnimationFrameId = window.requestAnimationFrame(update)
}
</script>

<template>
  <section
    class="rating-card h-[190px] w-full cursor-pointer rounded-[12px] border border-[rgba(5,21,36,0.08)] bg-[#fff] p-[20px] transition-[box-shadow] duration-[200ms] hover:shadow-[0_12px_32px_rgba(8,19,62,0.08)]"
    @click="openRatingDialog"
  >
    <h2
      class="m-0 flex h-[26px] items-center gap-[6px] text-[18px] leading-[26px] font-[700] text-[#292a2e]"
    >
      {{ t('82384') }}
    </h2>

    <div class="font-wiki mt-[20px] flex h-[49px] min-w-0 flex-wrap items-baseline gap-[6px]">
      <span
        v-if="animatedRatingScore !== emptyText"
        ref="ratingScoreRef"
        data-allow-mismatch="text"
        data-ssr-rating-score
        :data-rating-target="numericRatingScore"
        class="font-wiki-Bold rating-score-num"
        :style="{ color: ratingScoreColor }"
      >{{ animatedRatingScore }}</span>
      <span v-else class="font-wiki-Bold text-[40px] leading-[49px] font-[700] tracking-[0]" :style="{ color: ratingScoreColor }">{{ ratingScore }}</span>
      <span class="font-wiki-Medium text-[14px] leading-[49px] font-[500] text-[#7d818a]">/10</span>
    </div>

    <div
      class="rating-progress rating-progress-enter relative mt-[17px] h-[8px] overflow-hidden rounded-[999px] bg-[#ecedf0]"
      :style="ratingProgressStyle"
      aria-hidden="true"
    >
      <div
        class="rating-progress-fill rating-progress-fill-enter h-full rounded-[0] bg-[#2468de] transition-[width] duration-[800ms] ease-[ease]"
        :style="{ width: animatedProgressWidth, background: ratingProgressColor }"
      ></div>
    </div>

    <div class="mt-[12px] h-[18px]  text-[14px] leading-[18px] font-[400] text-[#7d818a]">
      {{ ratingDate }}
    </div>
  </section>

  <Teleport to="body">
    <Transition name="rating-dialog-fade">
      <section v-if="isRatingDialogOpen" class="fixed inset-0 z-[3000] flex items-center justify-center bg-[rgba(9,20,51,0.48)] p-[32px]" @click.self="closeRatingDialog">
        <div class="flex w-[min(850px,calc(100vw-48px))] h-[min(600px,calc(100vh-64px))] rounded-[28px] shadow-[0_24px_80px_rgba(8,19,62,0.18)]" role="dialog" aria-modal="true" aria-labelledby="rating-dialog-title">
          <aside class="flex w-[364px] shrink-0 flex-col rounded-[28px_0_0_28px] rtl:rounded-[0_28px_28px_0] bg-[#07132f] p-[48px_20px] text-white">
            <div class="flex flex-col gap-[16px]">
              <div class="m-0 text-[18px] font-[600] leading-[26px]">{{ t('82384') }}</div>
              <div class="flex items-center gap-[8px] h-[26px]">
                <CommonImage
                  class="!size-[20px] shrink-0 rounded-[2px]"
                  :src="ratingExchangeIcon"
                  alt=""
                />
                
                <div class="overflow-hidden truncate text-[18px] font-[600] leading-[26px]">{{ ratingExchangeName }}</div>
              </div>
            </div>

            <div class="flex items-end gap-[8px] mt-[30px]">
              <span class="font-wiki text-[40px] font-[700] leading-[38px]">{{ dialogRatingScore }}</span>
              <span class="font-wiki text-[14px] font-[500] leading-[22px]">/10</span>
              <span class="rounded-[2px] px-[8px] py-[2px] text-[11px] leading-[16px]" :style="{ backgroundColor: dialogRatingLevelBgColor }">{{ ratingLevel }}</span>
            </div>
            <p class="mt-[12px] text-[#b7b9be] text-[12px] leading-[18px]">{{ t('84466') }} <span dir="ltr">{{ dialogRatingDate }}</span></p>

            <div class="rating-dialog-radar relative w-full h-[260px] my-[30px]">
              <div ref="radarChartRef" class="w-full h-[260px]"></div>
              <div class="absolute inset-0 z-[1] flex items-center justify-center text-white text-[28px] font-[500] leading-[normal] pointer-events-none">{{ dialogRatingScore }}</div>
              <div ref="radarHotspotListRef" class="radar-score-hotspot-list absolute inset-0 z-[2] pointer-events-none">
                <button
                  v-for="item in radarScoreHotspots"
                  :key="item.label"
                  type="button"
                  :ref="(el) => setRadarHotspotRef(item.label, el)"
                  class="radar-score-hotspot absolute flex items-center justify-center border-0 bg-transparent text-white cursor-pointer pointer-events-auto text-center"
                  :class="[item.className, { 'radar-score-hotspot-active': activeRadarPopoverLabel === item.label }]"
                  :aria-expanded="activeRadarPopoverLabel === item.label"
                  @mouseenter="prepareRadarPopover(item.label)"
                  @focus="prepareRadarPopover(item.label)"
                  @click.stop="toggleRadarPopover(item.label)"
                >
                  <span class="radar-score-hotspot-content inline-flex min-w-[72px] flex-col items-center justify-center gap-[2px] px-[2px] py-[4px] border-0 rounded-[4px] transition-[background-color,border-color,box-shadow] duration-[160ms]">
                    <span class="radar-score-hotspot-title inline-flex max-w-full items-center justify-center gap-[2px]">
                      <span class="radar-score-hotspot-title-text text-white text-[12px] font-[400] leading-[18px]">{{ item.label }}</span>
                      <CommonImage
                        class="radar-score-hotspot-arrow !block !size-[14px]"
                        src="https://resources1.interface003.com/web30/2026/07/02/3815c9d8-3f2f-4963-942e-7d1626167bfd.png"
                        alt=""
                      />
                    </span>
                    <span class="radar-score-hotspot-row inline-flex max-w-full items-center justify-center gap-[2px]">
                      <strong class="font-wiki text-white text-[14px] font-[500] leading-[22px] whitespace-nowrap">{{ item.value }}</strong>
                      <span v-if="item.hasAdjustmentChange" class="radar-score-divider" aria-hidden="true"></span>
                      <span v-if="item.hasAdjustmentChange"  class="font-wiki radar-score-trend inline-flex items-center justify-center gap-[2px] leading-[18px] text-[12px] font-[500] whitespace-nowrap" :class="item.trendClass" :style="item.trendStyle">
                        <span>{{ item.trend }}</span>
                        <span class="inline-flex w-[16px] h-[18px] shrink-0 items-center justify-center overflow-visible leading-[0] align-middle" aria-hidden="true">
                          <CommonJsonLottie
                            v-if="item.hasAdjustmentAnimation"
                            class="block !size-[16px] shrink-0 origin-center"
                            :animation-data="item.adjustmentIcon"
                            width="16px"
                            height="16px"
                          />
                        </span>
                      </span>
                    </span>
                  </span>
                  <span
                    :ref="(el) => setRadarPopoverRef(item.label, el)"
                    class="radar-score-popover"
                    :data-placement="getRadarPopoverPlacement(item.label)"
                    :style="getRadarPopoverStyle(item.label)"
                    role="tooltip"
                    @click.stop
                  >
                    <span class="text-[#292a2e] text-[16px] font-[600] leading-[24px] whitespace-nowrap">{{ item.label }}</span>
                    <span class="flex items-center gap-[4px] mt-[-4px]">
                      <span class="font-wiki"><span class="text-[18px] font-[700] text-[#292A2E]">{{ item.value }}</span><span class="text-[12px]">/10</span></span>
                      <span class="inline-flex items-center rounded-[2px] text-white text-[11px] font-[400] leading-[16px] p-[2px_8px]" :style="item.levelStyle">{{ item.level }}</span>
                    </span>
                    <span class="radar-score-popover-track relative block w-full h-[12px] overflow-hidden rounded-[1000px] bg-[#f0f1f2]" aria-hidden="true">
                      <span
                        v-if="item.hasAdjustmentChange"
                        class="radar-score-popover-track-rest"
                        :class="{ 'radar-score-popover-track-rest-positive': item.adjustmentValue > 0 }"
                        :style="getRadarTrackRestStyle(item.totalValue, item.adjustmentValue)"
                      ></span>
                      <span
                        class="radar-score-popover-track-fill"
                        :style="getRadarTrackFillStyle(item.totalValue, item.adjustmentValue)"
                      ></span>
                    </span>
                    <span class="text-[#7d818a] text-[13px] font-[400] leading-[20px]">{{ item.description }}</span>
                    <template v-for="adjustment in item.adjustments" :key="adjustment.key">
                      <!-- 监管守护 -->
                      <button
                        v-if="adjustment.guardianTitle"
                        type="button"
                        class="flex flex-col gap-[12px] text-left cursor-pointer"
                        @click="openIapDialogFromRatingDialog"
                      >
                        <span class="flex items-end gap-[4px]">
                          <span class="text-[#292a2e] text-[16px] font-[600] leading-[24px] whitespace-nowrap">{{ adjustment.guardianTitle }}</span>
                          <span
                            v-if="adjustment.hasAdjustmentChange"
                            class="font-wiki inline-flex items-center text-[13px] font-[500] leading-[20px]"
                            :style="adjustment.trendStyle"
                          >
                            <span>{{ adjustment.trend }}</span>
                            <span class="inline-flex w-[16px] h-[18px] shrink-0 items-center justify-center overflow-visible leading-[0] align-middle" aria-hidden="true">
                              <CommonJsonLottie
                                v-if="adjustment.hasAdjustmentAnimation"
                                class="block !size-[16px] shrink-0 origin-center"
                                :animation-data="adjustment.adjustmentIcon"
                                width="16px"
                                height="16px"
                              />
                            </span>
                          </span>
                        </span>
                        <span class="text-[#7d818a] text-[13px] font-[400] leading-[20px]">{{ adjustment.guardianTips || '已加入IAP保障计划，开启赔付守护。' }}</span>
                        <span class="inline-flex h-[38px] items-center gap-[4px]">
                          <CommonImage v-if="adjustment.guardianAmountIcon" class="!size-[32px]" :src="adjustment.guardianAmountIcon" alt="" />
                          <span v-else class="inline-flex !size-[32px] items-center justify-center rounded-full bg-[#37a982] text-white text-[20px] font-[700] leading-[1]" aria-hidden="true"></span>
                          <strong class="text-[#292a2e] text-[32px] font-[700] leading-[38px] whitespace-nowrap">{{ adjustment.guardianAmount || '100,000' }}</strong>
                        </span>
                        <span v-if="adjustment.guardianItems.length" class="radar-score-popover-guardian-table" :style="adjustment.guardianTableStyle">
                          <span v-for="guardianItem in adjustment.guardianItems" :key="`${guardianItem.title}-${guardianItem.value}`">
                            <span>{{ guardianItem.title }}</span>
                            <strong :style="{ color: guardianItem.valueColor || undefined }">{{ guardianItem.value }}</strong>
                          </span>
                        </span>
                      </button>
                      <span v-if="adjustment.officeVisitSlides.length || adjustment.deductions.length" class="flex flex-col gap-[12px]">
                        <span class="radar-score-popover-deduct-title inline-flex items-end gap-[4px] text-[#292a2e] text-[14px] font-[500] leading-[22px]">
                          <span>{{ adjustment.adjustmentContent }}</span>
                          <span v-if="adjustment.hasAdjustmentChange" class="font-wiki inline-flex items-center gap-[2px]" :class="adjustment.trendClass">
                            <strong>{{ adjustment.trend }}</strong>
                            <span class="inline-flex w-[16px] h-[18px] shrink-0 items-center justify-center overflow-visible leading-[0] align-middle" aria-hidden="true">
                              <CommonJsonLottie
                                v-if="adjustment.hasAdjustmentAnimation"
                                class="block !size-[16px] shrink-0 origin-center"
                                :animation-data="adjustment.adjustmentIcon"
                                width="16px"
                                height="16px"
                              />
                            </span>
                          </span>
                        </span>
                        <span v-if="adjustment.officeVisitSlides.length && adjustment.officeVisitTitle" class="text-[#7D818A] text-[14px] font-[400] leading-[22px]">
                          {{ adjustment.officeVisitTitle }}
                        </span>
                        <span v-if="adjustment.officeVisitSlides.length" class="flex w-full flex-col items-center gap-[8px]">
                        <span
                          class="block overflow-hidden w-full rounded-[8px] cursor-grab touch-pan-y active:cursor-grabbing"
                          @pointerdown.stop="handleOfficeVisitPointerDown"
                          @pointerup.stop="handleOfficeVisitPointerUp($event, adjustment.officeVisitSlides.length)"
                          @pointercancel.stop="handleOfficeVisitPointerCancel"
                          @pointerleave.stop="handleOfficeVisitPointerCancel"
                        >
                          <span class="radar-score-popover-office-track flex w-full transition-transform duration-[260ms] will-change-transform" :style="officeVisitTrackStyle">
                            <span
                              v-for="slide in adjustment.officeVisitSlides"
                              :key="slide.id"
                              class="flex w-full min-w-full h-[94px] items-start gap-[12px] rounded-[8px] bg-[#f8f8f8] p-[12px]"
                            >
                              <span class="radar-score-popover-office-cover" aria-hidden="true">
                                <CommonImage v-if="slide.cover" class="!block !w-full !h-full object-cover" :src="slide.cover" alt="" />
                              </span>
                              <span class="flex min-w-0 self-stretch flex-1 flex-col justify-between">
                                <span class="radar-score-popover-office-title">
                                  {{ slide.title }}
                                </span>
                                <span class="flex items-center gap-[8px]">
                                  <span
                                    class="inline-flex items-center rounded-[2px] border text-[11px] font-[400] leading-[16px] p-[2px_8px] whitespace-nowrap"
                                    :style="{ backgroundColor: slide.label.backgroundColor, borderColor: slide.label.borderColor || 'transparent', color: slide.label.fontColor }"
                                  >{{ slide.label.text }}</span>
                                  <span class="inline-flex min-w-0 items-center gap-[4px] text-[#8c8f97] text-[12px] font-[400] leading-[18px] whitespace-nowrap">
                                    <CommonImage
                                      class="inline-block !size-[14px] shrink-0"
                                      src="https://resources1.interface003.com/web30/2026/07/02/28606e52-8a25-432d-86db-44113614f23d.png"
                                      alt=""
                                    />
                                    <span>{{ slide.location }}</span>
                                  </span>
                                </span>
                              </span>
                            </span>
                          </span>
                        </span>
                        <span
                          v-if="adjustment.officeVisitSlides.length > 1"
                          class="radar-score-popover-office-dots flex w-[54px] h-[4px] items-center justify-center gap-[3px]"
                          aria-label="办公地实勘切换"
                        >
                          <button
                            v-for="(_, index) in adjustment.officeVisitSlides"
                            :key="index"
                            type="button"
                            :class="{ 'radar-score-popover-office-dot-active': activeOfficeVisitIndex === index }"
                            :aria-label="`切换到第 ${index + 1} 条办公地实勘`"
                            :aria-current="activeOfficeVisitIndex === index"
                            @click.stop="toOfficeVisitSlide(index, adjustment.officeVisitSlides.length)"
                          ></button>
                        </span>
                      </span>
                        <span v-else-if="adjustment.deductions.length" class="flex flex-col gap-[12px]">
                          <span v-for="deduction in adjustment.deductions" :key="deduction" class="radar-score-popover-risk-item relative flex w-full items-center rounded-[8px] bg-[rgba(201,55,44,0.06)] text-[#e2483d] text-[12px] font-[400] leading-[18px] min-h-[34px] p-[8px_12px] whitespace-normal">
                          <CommonImage
                              class="!size-[16px] mr-[8px] rtl:mr-0 rtl:ml-[8px]"
                              src="https://resources1.interface003.com/web30/2026/07/02/8cb0035a-9e13-474b-96f8-916756a526a5.png"
                              alt=""
                            />
                          {{
                          deduction
                          }}</span>
                        </span>
                      </span>
                    </template>
                  </span>
                </button>
              </div>
            </div>

            <div class="flex items-center justify-center gap-[6px] mt-auto text-center text-[#b7b9be] text-[12px] font-[400] leading-[18px]" aria-label="数据来源">
              <span class="w-[34px] h-[1px] shrink-0 bg-[rgba(255,255,255,0.12)]" aria-hidden="true"></span>
              <CommonImage
                class="!size-[12px]"
                src='https://resources1.interface003.com/web30/2026/07/07/c38f0682-de03-4600-8d90-16ed9996516c.png'
                alt="logo"
              />
              <span class="whitespace-nowrap">{{ t('82547') }}</span>
              <span class="w-[34px] h-[1px] shrink-0 bg-[rgba(255,255,255,0.12)]" aria-hidden="true"></span>
            </div>
          </aside>

          <main class="rating-dialog-content relative flex flex-1 min-h-0 flex-col rounded-[0_28px_28px_0] rtl:rounded-[28px_0_0_28px] p-[32px] text-[#292a2e] bg-white">
            <CommonImage
              class="!absolute top-[24px] right-[24px] rtl:right-auto rtl:left-[26px] !size-[32px] cursor-pointer"
              src='https://resources1.interface003.com/web30/2026/07/02/e1866379-c778-4f76-9890-f2e3eb5820b8.png'
              alt="close"
              @click.stop="closeRatingDialog"
            />
            <h3>{{ t('82495') }}</h3>
            <div class="rating-dialog-scroll flex-1 min-h-0 overflow-y-auto">
              <p class="mt-[16px] mb-[24px] text-[#7d818a] text-[14px] leading-[22px]">
                {{ ratingDetail?.risk?.title || '' }}
              </p>

              <div v-for="card in trendChartCards" :key="card.key" class="rating-dialog-chart-card">
                <div class="rating-dialog-chart-head">
                  <h4>{{ card.title }}</h4>
                  <ElSelect
                    class="rating-dialog-chart-select"
                    popper-class="rating-dialog-chart-select-popper"
                    size="small"
                    :model-value="getTrendCardPeriod(card.key)"
                    :teleported="false"
                    @change="updateTrendPeriod(card.key, String($event))"
                    @click.stop
                  >
                    <ElOption
                      v-for="option in trendPeriodOptions"
                      :key="option.value"
                      :label="option.name"
                      :value="option.value"
                    ></ElOption>
                  </ElSelect>
                </div>
                <div class="rating-dialog-chart-wrap">
                  <div :ref="(el) => setTrendChartRef(card.key, el)" class="rating-dialog-chart"></div>
                  <div class="rating-dialog-legend">
                    <button
                      v-for="item in getTrendLegendItems(card)"
                      :key="item.name"
                      type="button"
                      :class="{ 'rating-dialog-legend-item-disabled': !isTrendLegendSelected(card.key, item.name) }"
                      :aria-pressed="isTrendLegendSelected(card.key, item.name)"
                      @click="toggleTrendLegend(card.key, item.name)"
                    >
                      <i :class="item.className"></i>{{ item.name }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </section>
    </Transition>
  </Teleport>
</template>

<style scoped lang="less">
.rating-progress::after {
  display: var(--rating-progress-notch-display);
  position: absolute;
  top: 0;
  left: var(--rating-progress-width);
  width: 6px;
  height: 100%;
  background: #fff;
  content: '';
  transform: translateX(-8px) skewX(-35deg);
  transform-origin: left center;
  pointer-events: none;
  transition: left 0.8s ease;
}

.rating-progress-enter::after {
  animation: rating-progress-notch-enter 0.8s ease both;
}

@keyframes rating-progress-notch-enter {
  from {
    left: 0;
  }

  to {
    left: var(--rating-progress-width);
  }
}

.rating-progress-fill {
  clip-path: var(--rating-progress-clip-path);
}

.rating-progress-fill-enter {
  animation: rating-progress-fill-enter 0.8s ease both;
}

@keyframes rating-progress-fill-enter {
  from {
    width: 0;
  }

  to {
    width: var(--rating-progress-width);
  }
}

html[dir='rtl'] .rating-card {
  .rating-progress::after {
    right: var(--rating-progress-width);
    left: auto;
    transform: translateX(8px) skewX(-35deg);
    transform-origin: right center;
    transition: right 0.8s ease;
  }
  .rating-progress-enter::after {
    animation-name: rating-progress-notch-enter-rtl;
  }
  .rating-progress-fill {
    margin-left: auto;
    clip-path: polygon(8px 0, 100% 0, 100% 100%, 0 100%);
  }
}

.radar-score-divider {
  position: relative;
  width: 4px;
  height: 10px;
  flex: 0 0 4px;
}

.radar-score-divider::before {
  position: absolute;
  top: 0;
  left: 1.5px;
  width: 1px;
  height: 10px;
  background: rgba(255, 255, 255, 0.72);
  content: '';
  transform: rotate(16deg);
  transform-origin: center;
}

.radar-score-trend-down {
  color: #e2483d;
}

.radar-score-trend-up {
  color: #1f845a;
}

.exchange-supported-currencies-card__change-icon-up {
  animation: exchange-supported-currencies-rise 1s ease-in-out infinite;
}

.exchange-supported-currencies-card__change-icon-down {
  animation: exchange-supported-currencies-fall 1s ease-in-out infinite;
}

@keyframes exchange-supported-currencies-rise {
  0% {
    transform: translateY(1.5px);
    opacity: 1;
  }

  50% {
    transform: translateY(-1.5px);
    opacity: 0.82;
  }

  100% {
    transform: translateY(1.5px);
    opacity: 1;
  }
}

@keyframes exchange-supported-currencies-fall {
  0% {
    transform: translateY(-1.5px);
    opacity: 1;
  }

  50% {
    transform: translateY(1.5px);
    opacity: 0.82;
  }

  100% {
    transform: translateY(-1.5px);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .exchange-supported-currencies-card__change-icon-up,
  .exchange-supported-currencies-card__change-icon-down,
  .rating-progress-fill-enter,
  .rating-progress-enter::after,
  .radar-score-popover-track-rest,
  .radar-score-popover-track-fill {
    animation: none;
  }

  .radar-score-popover-track-fill {
    transition: none;
  }

}

@keyframes rating-progress-notch-enter-rtl {
  from {
    right: 0;
  }

  to {
    right: var(--rating-progress-width);
  }
}

.radar-score-hotspot-0 {
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
}

.radar-score-hotspot-1 {
  top: 104px;
  right: -10px;
  transform: translateY(-50%);
}

.radar-score-hotspot-2 {
  left: calc(50% + 74px);
  bottom: -5px;
  transform: translateX(-50%);
}

.radar-score-hotspot-3 {
  bottom: -5px;
  left: calc(50% - 74px);
  transform: translateX(-50%);
}

.radar-score-hotspot-4 {
  top: 104px;
  left: -10px;
  transform: translateY(-50%);
}

.radar-score-hotspot-content {
  position: relative;
  z-index: 1;
  min-height: 48px;
}

.radar-score-hotspot-title {
  min-width: 0;
}

.radar-score-hotspot-title-text {
  min-width: 0;
  text-align: center;
  white-space: nowrap;
}

.radar-score-hotspot-arrow {
  flex: 0 0 14px;
  margin-top: 2px;
}

.radar-score-hotspot-row {
  min-height: 22px;
}

.radar-score-hotspot-0 .radar-score-hotspot-content,
.radar-score-hotspot-2 .radar-score-hotspot-content,
.radar-score-hotspot-3 .radar-score-hotspot-content {
  width: max-content;
  max-width: none;
}

.radar-score-hotspot-1 .radar-score-hotspot-content,
.radar-score-hotspot-4 .radar-score-hotspot-content {
  width: 76px;
  max-width: 76px;
}

.radar-score-hotspot-1 .radar-score-hotspot-title,
.radar-score-hotspot-4 .radar-score-hotspot-title {
  align-items: flex-start;
}

.radar-score-hotspot-1 .radar-score-hotspot-title-text,
.radar-score-hotspot-4 .radar-score-hotspot-title-text {
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.radar-score-hotspot {
  z-index: 1;
}

.radar-score-hotspot:hover,
.radar-score-hotspot:focus-visible,
.radar-score-hotspot-active {
  z-index: 100;
}

.radar-score-popover {
  position: absolute;
  z-index: 99;
  top: 0;
  left: 0;
  display: flex;
  width: 390px;
  flex-direction: column;
  gap: 12px;
  border: 0;
  border-radius: 4px;
  background: #fff;
  box-shadow:
    0 0 1px rgba(30, 31, 33, 0.12),
    0 0 8px rgba(30, 31, 33, 0.16);
  color: #505258;
  font-size: 13px;
  line-height: 20px;
  opacity: 0;
  overflow: hidden;
  padding: 20px;
  pointer-events: none;
  text-align: left;
  transform: translate(4px, 0);
  transition:
    opacity 0.16s ease,
    transform 0.16s ease;
  visibility: hidden;
  max-width: calc(100vw - 16px);
}

.radar-score-popover::after {
  position: absolute;
  top: var(--radar-popover-arrow-top, 24px);
  left: -5px;
  width: 10px;
  height: 10px;
  background: #fff;
  content: '';
  transform: rotate(45deg);
}

.radar-score-popover[data-placement='left'] {
  transform: translate(-4px, 0);
}

.radar-score-popover[data-placement='left']::after {
  right: -5px;
  left: auto;
}

.radar-score-popover-score {
strong {
    color: #1868db;
  }
}

.radar-score-popover-track-rest,
.radar-score-popover-track-fill {
  position: absolute;
  top: 50%;
  display: block;
  height: 12px;
  border-radius: 1000px;
  background-size: 24px 24px;
  transform: translateY(-50%);
}

.radar-score-popover-track-rest {
  right: 0;
  left: 0;
  width: 0;
  border: 1px dashed var(--radar-score-track-rest-border-color, rgba(11, 18, 40, 0.14));
  background:
    repeating-linear-gradient(
      116deg,
      rgba(255, 255, 255, 0.18) 0,
      rgba(255, 255, 255, 0.18) 5px,
      transparent 5px,
      transparent 12px
    ),
    var(--radar-score-track-rest-color, #dcdde0);
  transform: translateY(-50%);
  transform-origin: left center;
}

.radar-score-popover-track-fill {
  left: 0;
  width: 0;
  max-width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background:
    repeating-linear-gradient(
      116deg,
      rgba(255, 255, 255, 0.1) 0,
      rgba(255, 255, 255, 0.1) 5px,
      transparent 5px,
      transparent 12px
    ),
    #4688ec;
  transform: translateY(-50%);
  transform-origin: left center;
  will-change: transform, background-position;
}

.radar-score-popover-guardian-table {
  position: relative;
  display: flex;
  overflow: hidden;
  width: 100%;
  flex-direction: column;
  border: 1px solid transparent;
  border-radius: 8px 8px 0 0;
  box-shadow: inset 0 4px 5px rgba(255, 255, 255, 0.55);
}

.radar-score-popover-guardian-table > span {
  display: flex;
  min-height: 44px;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  color: #505258;
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
}

.radar-score-popover-guardian-table strong {
  color: #292a2e;
  font-size: 13px;
  font-weight: 500;
  line-height: 20px;
  text-align: right;
  white-space: nowrap;
}

.radar-score-popover-guardian-table > span:first-child strong {
  color: #1f845a;
}

.radar-score-popover-office-cover {
  position: relative;
  display: block;
  width: 105px;
  height: 70px;
  flex: 0 0 105px;
  overflow: hidden;
  border-radius: 4px;
}

.radar-score-popover-office-title {
  display: -webkit-box;
  overflow: hidden;
  color: #292a2e;
  font-size: 14px;
  font-weight: 500;
  line-height: 22px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.radar-score-popover-office-dots button {
  display: block;
  width: 4px;
  height: 4px;
  border: 0;
  border-radius: 4px;
  background: #d9d9d9;
  cursor: pointer;
  padding: 0;
}

.radar-score-popover-office-dots button:focus-visible {
  outline: 2px solid rgba(24, 104, 219, 0.5);
  outline-offset: 2px;
}

.radar-score-popover-office-dots .radar-score-popover-office-dot-active {
  width: 12px;
  background: #505258;
}

.radar-score-popover-deduct-title {
strong {
    font-size: 13px;
    font-weight: 500;
    line-height: 20px;
  }
}



.radar-score-hotspot:hover .radar-score-hotspot-content,
.radar-score-hotspot:focus-visible .radar-score-hotspot-content,
.radar-score-hotspot-active .radar-score-hotspot-content {
  background: rgba(255, 255, 255, 0.2);
}

.radar-score-hotspot:focus-visible {
  outline: none;
}

.radar-score-hotspot:hover .radar-score-popover,
.radar-score-hotspot:focus-visible .radar-score-popover,
.radar-score-hotspot-active .radar-score-popover {
  opacity: 1;
  pointer-events: auto;
  transform: translate(0, 0);
  visibility: visible;
}

.radar-score-hotspot:hover .radar-score-popover-track-rest,
.radar-score-hotspot:focus-visible .radar-score-popover-track-rest,
.radar-score-hotspot-active .radar-score-popover-track-rest {
  width: var(--radar-score-track-rest-width, 0%);
}

.radar-score-hotspot:hover .radar-score-popover-track-rest-positive,
.radar-score-hotspot:focus-visible .radar-score-popover-track-rest-positive,
.radar-score-hotspot-active .radar-score-popover-track-rest-positive {
  animation: radar-score-popover-track-rest-positive-enter 3s linear both;
}

.radar-score-hotspot:hover .radar-score-popover-track-fill,
.radar-score-hotspot:focus-visible .radar-score-popover-track-fill,
.radar-score-hotspot-active .radar-score-popover-track-fill {
  width: var(--radar-score-track-fill-width, 0%);
  animation:
    radar-score-popover-track-fill-enter 2s linear both,
    radar-score-popover-track-flow 2s linear infinite;
}

@keyframes radar-score-popover-track-fill-enter {
  from {
    width: 0;
  }

  to {
    width: var(--radar-score-track-fill-width, 0%);
  }
}

@keyframes radar-score-popover-track-rest-positive-enter {
  from {
    width: 0;
  }

  66.6667% {
    width: var(--radar-score-track-rest-sync-width, 0%);
  }

  to {
    width: var(--radar-score-track-rest-width, 0%);
  }
}

@keyframes radar-score-popover-track-flow {
  from {
    background-position: 0 0;
  }

  to {
    background-position: 24px 0;
  }
}

.rating-dialog-metric {
  position: absolute;
  display: grid;
  gap: 2px;
  width: 76px;
  color: #fff;
  font-size: 12px;
  line-height: 18px;

  strong {
    font-size: 14px;
    font-weight: 500;
    line-height: 22px;
  }

  em {
    font-style: normal;
  }
}

.rating-dialog-metric-0 {
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.rating-dialog-metric-1 {
  top: 96px;
  right: 0;
}

.rating-dialog-metric-2 {
  right: 42px;
  bottom: 0;
}

.rating-dialog-metric-3 {
  bottom: 0;
  left: 42px;
}

.rating-dialog-metric-4 {
  top: 96px;
  left: 0;
}

.rating-dialog-trend-down {
  color: #e2483d;
}

.rating-dialog-trend-up {
  color: #00a870;
}

.rating-dialog-content {
  h3 {
    margin: 0;
    border-bottom: 0.5px solid rgba(11, 18, 40, 0.08);
    padding-bottom: 16px;
    font-size: 18px;
    font-weight: 600;
    line-height: 28px;
  }
}

.rating-dialog-scroll {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.rating-dialog-chart-head {
  h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
    color: #292A2E;
  }

}

.rating-dialog-chart-select :deep(.el-select__wrapper) {
  position: relative;
  width: fit-content;
  min-height: 22px;
  height: 22px;
  gap: 4px;
  border-radius: 2px;
  background: #f8f8f8;
  box-shadow: none;
  cursor: pointer;
  padding: 2px 26px 2px 8px;
}

.rating-dialog-chart-select :deep(.el-select__wrapper.is-focused) {
  box-shadow: 0 0 0 2px rgba(24, 104, 219, 0.08);
}

.rating-dialog-chart-select :deep(.el-select__wrapper::after) {
  position: absolute;
  top: 50%;
  right: 8px;
  width: 14px;
  height: 14px;
  background: url('https://resources1.interface003.com/web30/2026/07/02/ef99bf00-e220-4b05-a38d-32a2f2322cd3.png')
    center / 14px 14px no-repeat;
  content: '';
  pointer-events: none;
  transform: translateY(-50%);
}

.rating-dialog-chart-select :deep(.el-select__selection) {
  width: max-content;
  flex: 0 0 auto;
  min-width: 0;
}

.rating-dialog-chart-select :deep(.el-select__selected-item) {
  color: #505258;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
}

.rating-dialog-chart-select :deep(.el-select__placeholder) {
  position: static;
  width: auto;
  overflow: visible;
  color: #505258;
  text-overflow: clip;
  transform: none;
}

.rating-dialog-chart-select :deep(.el-select__suffix),
.rating-dialog-chart-select :deep(.el-select__caret),
.rating-dialog-chart-select :deep(.el-select__icon) {
  display: none;
}

:deep(.rating-dialog-chart-select-popper .el-select-dropdown__list) {
  padding: 4px 0;
}

:deep(.rating-dialog-chart-select-popper.el-select__popper.el-popper) {
  width: max-content !important;
  min-width: 100px !important;
  border: none;
  box-shadow:
    0 0 1px 0 rgba(30, 31, 33, 0.31),
    0 8px 12px 0 rgba(30, 31, 33, 0.15);
}

:deep(.rating-dialog-chart-select-popper .el-select-dropdown__item){
  height: 32px;
  line-height: 22px;
  padding: 5px 12px;
  text-align: center;
  color: #292A2E;
  font-size: 14px;
}

:deep(.rating-dialog-chart-select-popper .el-select-dropdown__item.is-selected){
  background: #F8F8F8;
  font-weight: 400;
}

:deep(.rating-dialog-chart-select-popper .el-select-dropdown__item.is-hovering:not(.is-selected):not(:hover)) {
  background-color: transparent;
}



.rating-dialog-chart-grid {
  position: absolute;
  inset: 8px 0 20px;
  background:
    repeating-linear-gradient(to right, transparent 0 67px, rgba(11, 18, 40, 0.08) 68px),
    repeating-linear-gradient(to bottom, transparent 0 29px, rgba(11, 18, 40, 0.08) 30px);
}

.rating-dialog-chart-line {
  position: absolute;
  inset: 24px 10px auto;
  height: 92px;
  border: 0;
  background: transparent;
}

.rating-dialog-chart-line::before {
  position: absolute;
  inset: 0;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  content: '';
  transform: skewY(-10deg);
}

.rating-dialog-chart-line-blue {
  color: #4688ec;
  transform: translateY(14px);
}

.rating-dialog-chart-line-orange {
  color: #f68909;
  transform: translateY(38px) scaleY(-1);
}

.rating-dialog-chart-popover {
  position: absolute;
  top: 20px;
  left: 210px;
  display: grid;
  gap: 4px;
  border-radius: 4px;
  background: #292a2e;
  padding: 8px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 12px;
  line-height: 18px;

  strong {
    color: #fff;
    font-size: 14px;
    line-height: 22px;
  }
}

.rating-dialog-chart-card {
  overflow: hidden;
  margin-top: 24px;
  border: 0.5px solid rgba(11, 18, 40, 0.14);
  border-radius: 8px;
  padding: 16px;
}

.rating-dialog-chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;

  h4 {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    line-height: 26px;
    color: #292A2E;
  }

}

.rating-dialog-chart-select {
  width: fit-content;
  height: 22px;
  flex: 0 0 auto;
}

.rating-dialog-chart-wrap {
  position: relative;
  height: 208px;
  margin-top: 16px;
}

.rating-dialog-chart {
  position: relative;
  height: 208px;
}

.rating-dialog-legend {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  height: 16px;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #505258;
  font-size: 11px;
  line-height: 16px;

  button {
    display: inline-flex;
    height: 16px;
    align-items: center;
    gap: 8px;
    border: 0;
    background: transparent;
    color: inherit;
    cursor: pointer;
    font: inherit;
    line-height: 16px;
    padding: 0;
    transition: opacity 0.16s ease;
  }

  button:focus-visible {
    outline: 2px solid rgba(24, 104, 219, 0.38);
    outline-offset: 2px;
  }

  i {
    display: block;
    width: 10px;
    height: 2px;
    flex: 0 0 10px;
    border-radius: 45px;
  }
}

.rating-dialog-legend-item-disabled {
  opacity: 0.35;
}

.rating-dialog-legend-blue {
  background: #0081ff;
}

.rating-dialog-legend-orange {
  background: #f68909;
}

.rating-dialog-fade-enter-active,
.rating-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.rating-dialog-fade-enter-active .rating-dialog,
.rating-dialog-fade-leave-active .rating-dialog {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.rating-dialog-fade-enter-from,
.rating-dialog-fade-leave-to {
  opacity: 0;
}

.rating-dialog-fade-enter-from .rating-dialog,
.rating-dialog-fade-leave-to .rating-dialog {
  opacity: 0;
  transform: translateY(8px) scale(0.98);
}

/* RTL support for Arabic layout - mirrors all directional properties */
html[dir='rtl'] {
  .radar-score-hotspot-arrow {
    transform: rotateY(180deg);
  }

  /* -- Dialog panel & content border-radius -- */
  .rating-dialog-score-panel {
    border-radius: 0 28px 28px 0;
  }
  .rating-dialog-content {
    border-radius: 28px 0 0 28px;
  }

  /* -- Close button -- */
  .rating-dialog-close {
    right: auto;
    left: 26px;
  }

  /* -- Chart select dropdown arrow -- */
  .rating-dialog-chart-select :deep(.el-select__wrapper) {
    padding: 2px 8px 2px 26px;
  }
  .rating-dialog-chart-select :deep(.el-select__wrapper::after) {
    right: auto;
    left: 8px;
  }

  /* -- Radar hotspot positions (swap left/right) -- */
  .radar-score-hotspot-0 {
    right: 50%;
    left: auto;
    transform: translateX(50%);
  }
  .radar-score-hotspot-1 {
    right: auto;
    left: -10px;
    transform: translateY(-50%);
  }
  .radar-score-hotspot-2 {
    right: calc(50% - 74px);
    left: auto;
    bottom: -8px;
    transform: translateX(50%);
  }
  .radar-score-hotspot-3 {
    right: calc(50% + 74px);
    left: auto;
    bottom: -8px;
    transform: translateX(50%);
  }
  .radar-score-hotspot-4 {
    right: -10px;
    left: auto;
    transform: translateY(-50%);
  }

  /* -- Radar popover: align to right edge -- */
  .radar-score-popover {
    right: 0;
    left: auto;
    text-align: right;
    transform: translate(-4px, 0);
  }
  .radar-score-popover::after {
    right: -5px;
    left: auto;
  }

  /* -- Radar popover left placement: align to left edge -- */
  .radar-score-popover[data-placement='left'] {
    transform: translate(4px, 0);
  }
  .radar-score-popover[data-placement='left']::after {
    right: auto;
    left: -5px;
  }

  /* -- Radar popover hover: reset transform direction -- */
  .radar-score-hotspot:hover .radar-score-popover,
  .radar-score-hotspot:focus-visible .radar-score-popover,
  .radar-score-hotspot-active .radar-score-popover {
    transform: translate(0, 0);
  }

  /* -- Progress track: mirror transform-origin -- */
  .radar-score-popover-track-rest {
    transform-origin: right center;
  }
  .radar-score-popover-track-fill {
    left: auto;
    right: 0;
    transform-origin: right center;
  }

  /* -- Guardian table: mirror text-align -- */
  .radar-score-popover-guardian-table strong {
    text-align: left;
  }

  /* -- Risk item: swap margin direction -- */
  .radar-score-popover-risk-item .radar-score-popover-risk-item-icon {
    margin-right: 0;
    margin-left: 8px;
  }

  /* -- Dialog metric positions (swap left/right) -- */
  .rating-dialog-metric-0 {
    left: auto;
    right: 50%;
    transform: translateX(50%);
  }
  .rating-dialog-metric-1 {
    right: auto;
    left: 0;
  }
  .rating-dialog-metric-2 {
    right: auto;
    left: 42px;
  }
  .rating-dialog-metric-3 {
    right: 42px;
    left: auto;
  }
  .rating-dialog-metric-4 {
    right: 0;
    left: auto;
  }

  /* -- Mobile breakpoint RTL -- */
  @media screen and (max-width: 799px) {
    .rating-dialog-score-panel {
      border-radius: 20px 20px 0 0;
    }
    .rating-dialog-content {
      border-radius: 0 0 20px 20px;
    }
  }
}

@media screen and (max-width: 799px) {
  .rating-dialog-mask {
    padding: 16px;
  }

  .rating-dialog {
    display: block;
    width: 100%;
    height: calc(100vh - 32px);
    border-radius: 20px;
    overflow-y: auto;
  }

  .rating-dialog-score-panel {
    width: 100%;
    min-height: 520px;
    border-radius: 20px 20px 0 0;
  }

  .rating-dialog-content {
    border-radius: 0 0 20px 20px;
    padding: 24px;
  }

  .rating-dialog-scroll {
    flex: none;
    height: auto;
    min-height: auto;
    overflow-y: visible;
  }
}

.rating-score-num {
  font-size: 40px;
  font-weight: 700;
  line-height: 49px;
  color: #292a2e;
  font-variant-numeric: tabular-nums;
}
.adaptive-image{
  background:none;
}
</style>
